import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { MeAPI } from '../mock';
import { useAuth } from './AuthContext';

// Shape of a garage car: { id, make, model, year, nickname, vehicle_id, is_primary }
// Anonymous visitors keep their cars in localStorage. Signed-in customers get
// a server-side list keyed to their user_id so it follows them across devices.

const STORAGE_KEY = 'tai_my_garage_v1';
const ACTIVE_KEY = 'tai_my_garage_active_v1';

function readLocal() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}
function writeLocal(cars) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cars || [])); } catch { /* quota */ }
}
function readActive() {
  try { return localStorage.getItem(ACTIVE_KEY) || ''; } catch { return ''; }
}
function writeActive(id) {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch { /* quota */ }
}

const GarageContext = createContext({
  cars: [],
  activeCar: null,
  loading: false,
  setActive: () => {},
  addCar: async () => {},
  updateCar: async () => {},
  removeCar: async () => {},
  reload: async () => {},
});

export function GarageProvider({ children }) {
  const { user } = useAuth();
  const [cars, setCars] = useState(() => readLocal());
  const [activeId, setActiveId] = useState(() => readActive());
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setCars(readLocal());
      return;
    }
    setLoading(true);
    try {
      const list = await MeAPI.garage();
      setCars(list || []);
    } catch {
      setCars([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load from server when user signs in; from localStorage otherwise.
  useEffect(() => { reload(); }, [reload]);

  // Keep localStorage in sync whenever cars change for anonymous users.
  useEffect(() => {
    if (!user) writeLocal(cars);
  }, [cars, user]);

  // When active car changes, persist it (works for both auth modes).
  useEffect(() => { writeActive(activeId); }, [activeId]);

  // If the currently active car no longer exists, clear the selection.
  useEffect(() => {
    if (!activeId) return;
    if (cars.length > 0 && !cars.some(c => c.id === activeId)) {
      // Fall back to a primary car if one is set, else no selection.
      const primary = cars.find(c => c.is_primary);
      setActiveId(primary ? primary.id : '');
    }
  }, [cars, activeId]);

  const addCar = useCallback(async (payload) => {
    const car = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      make: (payload.make || '').trim(),
      model: (payload.model || '').trim(),
      year: payload.year ? Number(payload.year) : null,
      nickname: (payload.nickname || '').trim(),
      vehicle_id: (payload.vehicle_id || '').trim(),
      fuel_type: (payload.fuel_type || '').trim().toLowerCase(),
      is_primary: !!payload.is_primary || cars.length === 0,
    };
    if (!car.make) throw new Error('Make is required');

    if (user) {
      const saved = await MeAPI.garageAdd({
        make: car.make, model: car.model, year: car.year,
        nickname: car.nickname, vehicle_id: car.vehicle_id,
        fuel_type: car.fuel_type, is_primary: car.is_primary,
      });
      setCars(prev => {
        const next = car.is_primary ? prev.map(c => ({ ...c, is_primary: false })) : prev;
        return [saved, ...next];
      });
      setActiveId(saved.id);
      return saved;
    }
    setCars(prev => {
      const next = car.is_primary ? prev.map(c => ({ ...c, is_primary: false })) : prev;
      return [car, ...next];
    });
    setActiveId(car.id);
    return car;
  }, [cars.length, user]);

  const updateCar = useCallback(async (id, patch) => {
    if (user) {
      await MeAPI.garageUpdate(id, patch);
    }
    setCars(prev => {
      let next = prev.map(c => (c.id === id ? { ...c, ...patch } : c));
      if (patch.is_primary) {
        next = next.map(c => (c.id === id ? c : { ...c, is_primary: false }));
      }
      return next;
    });
  }, [user]);

  const removeCar = useCallback(async (id) => {
    if (user) {
      try { await MeAPI.garageRemove(id); } catch { /* keep going */ }
    }
    setCars(prev => prev.filter(c => c.id !== id));
    if (activeId === id) setActiveId('');
  }, [user, activeId]);

  const activeCar = cars.find(c => c.id === activeId) || null;

  const value = {
    cars,
    activeCar,
    activeId,
    loading,
    setActive: (id) => setActiveId(id || ''),
    addCar,
    updateCar,
    removeCar,
    reload,
  };

  return <GarageContext.Provider value={value}>{children}</GarageContext.Provider>;
}

export const useGarage = () => useContext(GarageContext);
